/**
 * Runner identity COLLECTION MANIFEST: the canonical schema and the one validation routine shared by the API
 * (POST /v1/operator/manifest/validate) and the web Operator page (client-side fallback in demo mode).
 *
 * Fields follow docs/NFT_SPEC.md (§1 properties, §7 open production decisions) and the `identity` section of
 * deployments/production.params.example.json: capacity, the fixed backing B in RUNNER base units, the numbering and
 * art-assignment policy, the trait seed commitment, mint activation (intake) and labels. Validation never trusts a
 * label: a fixture or prototype manifest, a manifest that reuses the labelled local fixture seed, or one whose own
 * comment says it is not approved can never come out as approved. Even an approved manifest is activated only by a
 * delayed SetIdentityIntake governance release; nothing here publishes anything.
 *
 * Self-contained on purpose (only zod): index.ts re-exports it, so it must not import from index.ts.
 */
import { z } from 'zod';

export const MANIFEST_SCHEMA_ID = 'runner.identity-manifest.v1';
export const MANIFEST_NUMBERING = 'sequential-from-1';
export const MANIFEST_ART_ASSIGNMENT = 'derived:IdentityTraits(seed,id)';
export const MANIFEST_RENDERER = 'base v1';

/**
 * Largest collection capacity RunnerRewards accepts: per-account Runner counts are uint16 (constructor reverts with
 * CapTooLarge above it). IDs themselves are uint256 and never reused.
 */
export const MANIFEST_MAX_CAP = 65_535;
const U128_MAX = (1n << 128n) - 1n;
const E18 = 10n ** 18n;
const BYTES32 = /^0x[0-9a-fA-F]{64}$/;
const POSITIVE_INT = /^[1-9]\d*$/;
/** Keys that would turn a collectible into a yield or market product (docs/NFT_SPEC.md §6). */
const FORBIDDEN_KEYS = ['rarityYield', 'yieldMultiplier', 'floorPrice', 'marketplace'] as const;
/** Words in a manifest's own comment/label that mark it as not approved. */
const NOT_APPROVED_LABEL = /\b(fixture|not approved|unapproved|prototype|example|local[- ]only)\b/i;

export const ManifestApprovalStatus = z.enum(['fixture', 'prototype', 'approved']);

/** Owner approval record. Incomplete records are not structural errors: they simply are not an approval. */
export const ManifestApproval = z
  .object({
    status: ManifestApprovalStatus,
    approvers: z.array(z.string().min(1).max(200)).max(32).optional(),
    reference: z.string().max(500).optional(),
  })
  .strict();

/**
 * Canonical collection manifest. `backing` is B in RUNNER base units (18 decimals) as a decimal string and must be a
 * whole number of RUNNER ≤ uint128; `cap` counts every ID ever issued (≤ MANIFEST_MAX_CAP, IDs are never reused);
 * `traitSeed` is the bytes32 commitment the base traits derive from; `intake` is the mint activation plan: always
 * `closed` in a manifest — intake opens only through a delayed SetIdentityIntake governance release after approval.
 */
export const CollectionManifest = z
  .object({
    $comment: z.string().max(2000).optional(),
    schema: z.literal(MANIFEST_SCHEMA_ID),
    name: z.string().min(1).max(64),
    symbol: z.string().min(1).max(16).optional(),
    backing: z
      .string()
      .regex(POSITIVE_INT, 'positive decimal integer string (RUNNER base units)')
      // zod runs refinements after a failed regex too: guard before BigInt.
      .refine((v) => POSITIVE_INT.test(v) && BigInt(v) <= U128_MAX, 'backing must fit in uint128')
      .refine((v) => POSITIVE_INT.test(v) && BigInt(v) % E18 === 0n, 'backing must be a whole number of RUNNER'),
    cap: z.number().int().min(1).max(MANIFEST_MAX_CAP),
    traitSeed: z.string().regex(BYTES32, 'bytes32 hex'),
    numbering: z.literal(MANIFEST_NUMBERING),
    artAssignment: z.literal(MANIFEST_ART_ASSIGNMENT),
    renderer: z.literal(MANIFEST_RENDERER),
    intake: z.literal('closed'),
    mintPrice: z.union([z.literal('0'), z.literal(0)]).optional(),
    royaltyBps: z.literal(0).optional(),
    approval: ManifestApproval.optional(),
  })
  .strict();
export type CollectionManifest = z.infer<typeof CollectionManifest>;

export const ManifestSeverity = z.enum(['error', 'warning', 'info']);
export type ManifestSeverity = z.infer<typeof ManifestSeverity>;

/** One validation finding. `id` is stable (clients and tests key on it); `field` names the manifest key, if any. */
export const ManifestFinding = z.object({ id: z.string(), severity: ManifestSeverity, field: z.string().nullable(), message: z.string() });
export type ManifestFinding = z.infer<typeof ManifestFinding>;

/** One rule of the checklist (every rule is listed, passed or not). */
export const ManifestCheck = z.object({ id: z.string(), label: z.string(), ok: z.boolean(), detail: z.string() });
export type ManifestCheck = z.infer<typeof ManifestCheck>;

export const ManifestStatus = z.enum(['invalid', 'fixture', 'prototype', 'approved']);
export type ManifestStatus = z.infer<typeof ManifestStatus>;

export const ManifestValidation = z.object({
  status: ManifestStatus,
  /** Every structural rule passed (CollectionManifest parses). */
  structurallyValid: z.boolean(),
  /** Owner approval recorded (≥ 2 approvers and a reference), not the fixture seed, not labelled as unapproved. */
  approved: z.boolean(),
  /** structurallyValid && approved. Publication still happens only through a delayed governance release. */
  publishable: z.boolean(),
  reason: z.string(),
  checks: z.array(ManifestCheck),
  findings: z.array(ManifestFinding),
});
export type ManifestValidationResult = z.infer<typeof ManifestValidation>;

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const show = (v: unknown): string => (v === undefined ? 'missing' : typeof v === 'string' ? v : JSON.stringify(v));

/**
 * Validate a collection manifest. Pure and deterministic; `fixtureSeed` is the labelled local fixture seed
 * (keccak256("RUNNER identity trait seed - local fixture"), @runner/simulation IDENTITY_TRAIT_SEED), which is refused
 * for anything but a fixture.
 */
export function validateCollectionManifest(input: unknown, opts: { fixtureSeed: string }): ManifestValidationResult {
  const checks: ManifestCheck[] = [];
  const findings: ManifestFinding[] = [];
  const add = (id: string, label: string, ok: boolean, detail: string, field: string | null) => {
    checks.push({ id, label, ok, detail });
    if (!ok) findings.push({ id, severity: 'error', field, message: `${label}: ${detail}` });
  };
  const m: Record<string, unknown> = isRecord(input) ? input : {};
  if (!isRecord(input)) {
    findings.push({ id: 'not-object', severity: 'error', field: null, message: 'The manifest must be a JSON object' });
  }

  add('schema', `Schema ${MANIFEST_SCHEMA_ID}`, m.schema === MANIFEST_SCHEMA_ID, show(m.schema), 'schema');
  const nameOk = typeof m.name === 'string' && m.name.trim().length > 0 && m.name.length <= 64;
  add('name', 'Collection label (name, 1–64 characters)', nameOk, show(m.name), 'name');

  let backing: bigint | null = null;
  if (typeof m.backing === 'string' && POSITIVE_INT.test(m.backing)) backing = BigInt(m.backing);
  add(
    'backing',
    'Fixed backing B (RUNNER base units, > 0, ≤ uint128, whole RUNNER)',
    backing !== null && backing <= U128_MAX && backing % E18 === 0n,
    backing === null ? `${show(m.backing)} (not a positive decimal integer string)` : `${backing / E18} RUNNER${backing % E18 === 0n ? '' : ' + a fraction'}`,
    'backing',
  );
  const cap = typeof m.cap === 'number' ? m.cap : NaN;
  add(
    'cap',
    `Collection capacity (counts every ID ever issued; 1–${MANIFEST_MAX_CAP.toLocaleString('en-US')}, RunnerRewards CapTooLarge)`,
    Number.isInteger(cap) && cap > 0 && cap <= MANIFEST_MAX_CAP,
    Number.isFinite(cap) ? String(cap) : show(m.cap),
    'cap',
  );
  const seedOk = typeof m.traitSeed === 'string' && BYTES32.test(m.traitSeed);
  add(
    'seed',
    'Trait seed committed (bytes32)',
    seedOk,
    seedOk ? String(m.traitSeed) : m.traitSeed === undefined ? 'missing — the planner would substitute the labelled local fixture seed' : `${show(m.traitSeed)} (malformed)`,
    'traitSeed',
  );
  add('numbering', 'Stable, never-reused IDs from 1', m.numbering === MANIFEST_NUMBERING, show(m.numbering), 'numbering');
  add('art', 'Art assignment derived only from seed and ID', m.artAssignment === MANIFEST_ART_ASSIGNMENT, show(m.artAssignment), 'artAssignment');
  add('renderer', 'Stable base renderer fallback', m.renderer === MANIFEST_RENDERER, show(m.renderer), 'renderer');
  add(
    'intake',
    'Mint activation planned closed (opened only by a delayed SetIdentityIntake release)',
    m.intake === 'closed',
    m.intake === 'open' ? 'open — a manifest cannot activate minting' : show(m.intake),
    'intake',
  );
  add('noPrice', 'No cash mint charge (minting is a deposit of B)', m.mintPrice === undefined || m.mintPrice === '0' || m.mintPrice === 0, m.mintPrice === undefined ? 'none' : show(m.mintPrice), 'mintPrice');
  add('noRoyalty', 'No required royalties', m.royaltyBps === undefined || m.royaltyBps === 0, m.royaltyBps === undefined ? 'none' : `${show(m.royaltyBps)} bps`, 'royaltyBps');
  const forbidden = FORBIDDEN_KEYS.filter((k) => k in m);
  add('noYield', 'No rarity yield, multipliers, floor prices or marketplace', forbidden.length === 0, forbidden.length ? forbidden.join(', ') : 'none', forbidden[0] ?? null);

  // Anything the canonical schema does not know (beyond the explicit rules above) is a structural error too.
  const parsed = CollectionManifest.safeParse(input);
  if (!parsed.success) {
    const known = new Set(checks.filter((c) => !c.ok).map((c) => c.id));
    for (const issue of parsed.error.issues) {
      const field = issue.path.length ? String(issue.path[0]) : null;
      if (issue.code === 'unrecognized_keys') {
        const keys = (issue as { keys?: string[] }).keys ?? [];
        const extra = keys.filter((k) => !(FORBIDDEN_KEYS as readonly string[]).includes(k));
        if (extra.length) findings.push({ id: 'unknown-field', severity: 'error', field: issue.path.length ? issue.path.join('.') : extra[0], message: `Unknown field(s): ${extra.join(', ')}` });
        continue;
      }
      const ruleFor: Record<string, string> = { schema: 'schema', name: 'name', backing: 'backing', cap: 'cap', traitSeed: 'seed', numbering: 'numbering', artAssignment: 'art', renderer: 'renderer', intake: 'intake', mintPrice: 'noPrice', royaltyBps: 'noRoyalty' };
      if (field && known.has(ruleFor[field] ?? '')) continue; // already reported by its rule
      findings.push({ id: field === 'approval' ? 'approval-shape' : `shape-${field ?? 'root'}`, severity: 'error', field: issue.path.join('.') || null, message: `${issue.path.join('.') || 'manifest'}: ${issue.message}` });
    }
  }
  const structurallyValid = parsed.success && checks.every((c) => c.ok);

  // Approval: labels are never trusted on their own.
  const approval = isRecord(m.approval) ? m.approval : {};
  const status = approval.status;
  const approvers = Array.isArray(approval.approvers) ? approval.approvers.filter((x) => typeof x === 'string' && x.trim().length > 0) : [];
  const reference = typeof approval.reference === 'string' ? approval.reference.trim() : '';
  const usesFixtureSeed = typeof m.traitSeed === 'string' && m.traitSeed.toLowerCase() === opts.fixtureSeed.toLowerCase();
  const labels = [m.$comment, m.name, approval.reference].filter((x): x is string => typeof x === 'string');
  const labelledNotApproved = labels.some((t) => NOT_APPROVED_LABEL.test(t));
  checks.push({
    id: 'fixtureSeed',
    label: 'Not the labelled local fixture seed',
    ok: !usesFixtureSeed,
    detail: usesFixtureSeed ? 'keccak256("RUNNER identity trait seed - local fixture")' : m.traitSeed === undefined ? 'no seed committed' : 'distinct seed',
  });
  const recorded = status === 'approved' && new Set(approvers.map((a) => (a as string).trim().toLowerCase())).size >= 2 && reference.length > 0;
  const approved = recorded && !usesFixtureSeed && !labelledNotApproved && m.traitSeed !== undefined;
  checks.push({
    id: 'approval',
    label: 'Owner approval recorded (≥ 2 distinct approvers and a reference)',
    ok: approved,
    detail:
      status === 'approved'
        ? `${approvers.length} approver(s)${reference ? '' : ', no reference'}${usesFixtureSeed ? '; fixture seed refused' : ''}${labelledNotApproved ? '; its own label says it is not approved' : ''}`
        : `status: ${show(status)}`,
  });

  const isFixture = status === 'fixture' || usesFixtureSeed || (typeof m.$comment === 'string' && /\bfixture\b/i.test(m.$comment)) || m.traitSeed === undefined;
  if (usesFixtureSeed) findings.push({ id: 'fixture-seed', severity: 'error', field: 'traitSeed', message: 'Uses the labelled local fixture trait seed; a production seed must come from an approved commitment' });
  if (isFixture) findings.push({ id: 'fixture', severity: 'error', field: 'approval.status', message: 'Fixture manifest: NOT approved and can never be published' });
  if (status === 'approved' && labelledNotApproved) {
    findings.push({ id: 'labelled-not-approved', severity: 'error', field: '$comment', message: 'Marked approved, but its own label/comment says fixture, prototype or not approved' });
  }
  if (!approved) {
    const why =
      status === 'approved'
        ? 'the approval record is incomplete or contradicted (needs ≥ 2 distinct approvers, a reference, a non-fixture committed seed and no unapproved label)'
        : `approval status is ${show(status)}`;
    findings.push({ id: 'not-approved', severity: 'error', field: 'approval', message: `NOT approved: ${why}` });
  } else {
    findings.push({ id: 'activation-by-governance', severity: 'info', field: 'intake', message: 'Approved manifest: intake still opens only through a delayed SetIdentityIntake governance release' });
  }
  if (status !== undefined && !ManifestApprovalStatus.safeParse(status).success) {
    findings.push({ id: 'approval-status', severity: 'warning', field: 'approval.status', message: `Unknown approval status ${show(status)} (expected fixture | prototype | approved)` });
  }

  const st: ManifestStatus = !structurallyValid ? 'invalid' : approved ? 'approved' : isFixture ? 'fixture' : 'prototype';
  const publishable = structurallyValid && approved;
  const reason = publishable
    ? 'Approved manifest. Publication still goes through the delayed governance release (SetIdentityIntake), never from this page.'
    : st === 'invalid'
      ? `Invalid manifest: fix the failed checks.${isFixture ? ' It is also a fixture: NOT approved.' : ' NOT approved.'}`
      : st === 'fixture'
        ? 'NOT approved: this is the labelled development fixture. Fixture manifests cannot be published.'
        : 'NOT approved: prototype manifests cannot be published.';
  return { status: st, structurallyValid, approved, publishable, reason, checks, findings };
}

/** The labelled development fixture (contracts/script/DefaultConfig.sol; deployments/production.params.example.json). */
export function fixtureCollectionManifest(seed: string): CollectionManifest {
  return {
    schema: MANIFEST_SCHEMA_ID,
    name: 'RUNNER Identity',
    backing: (100_000n * E18).toString(),
    cap: 1000,
    traitSeed: seed,
    numbering: MANIFEST_NUMBERING,
    artAssignment: MANIFEST_ART_ASSIGNMENT,
    renderer: MANIFEST_RENDERER,
    intake: 'closed',
    approval: { status: 'fixture', approvers: [], reference: 'DefaultConfig.sol (local fixture, not approved)' },
  };
}

/** POST /v1/operator/manifest/validate body. */
export const ManifestValidateRequest = z
  .object({ manifest: z.unknown().describe('A CollectionManifest. Any JSON value is accepted and reported on (findings), never rejected for its content.') })
  .strict();

export const ManifestValidateResponse = ManifestValidation.extend({
  previewOnly: z.literal(true),
  schemaId: z.literal(MANIFEST_SCHEMA_ID),
  /** The labelled local fixture seed the manifest was checked against. */
  fixtureSeed: z.string(),
  /** The deployed collection at the pinned block, when the chain is reachable (null otherwise). */
  deployed: z
    .object({ blockNumber: z.string(), backing: z.string(), cap: z.number(), intakeOpen: z.boolean(), matchesManifest: z.boolean() })
    .nullable(),
});
export type ManifestValidateResponse = z.infer<typeof ManifestValidateResponse>;
